import { FormFieldConfig } from "Components/Common/CommonForm";
import { Column } from "Components/Common/CommonTableIndexWise";
import { parseLocalDate } from "common/service/CommonService";

/* ------------------------------------------------------------------------ */
/* STATIC OPTIONS                                                           */
/* ------------------------------------------------------------------------ */

export const STORAGE_LOCATION_OPTIONS = [
  { label: "Pharmacy Room 1", value: "Pharmacy Room 1" },
  { label: "Cold Room A", value: "Cold Room A" },
  { label: "Main Store Room", value: "Main Store Room" },
  { label: "Deep Freeze Area", value: "Deep Freeze Area" },
];

export const STORAGE_EQUIPMENT_OPTIONS = [
  { label: "Refrigerator 01", value: "Refrigerator 01" },
  { label: "Deep Freezer -20°C", value: "Deep Freezer -20°C" },
  { label: "Ambient Cabinet 02", value: "Ambient Cabinet 02" },
  { label: "Ultra Low Freezer -80°C", value: "Ultra Low Freezer -80°C" },
];

export const STORAGE_STATUS_OPTIONS = [
  { label: "Active", value: "Active" },
  { label: "Quarantine", value: "Quarantine" },
  { label: "In Use", value: "In Use" },
  { label: "Archived", value: "Archived" },
];

/* ------------------------------------------------------------------------ */
/* TYPES                                                                    */
/* ------------------------------------------------------------------------ */

export interface StorageContainerItem {
  ipdetailid?: number | string; // links back to the source IP inventory container row
  containernumber: string;
  containerquantity: number | string;
  storagelocation: string | null;
  storageequipment: string | null;
  shelfrack?: string | null;
  position?: string | null;
  storagestatus: string | null;
  remarks?: string | null;
}

export interface IpStorageFormValues {
  studycode: string;
  sponsorname: string;
  ipcategory: string;
  ipid: string; // selected inventory record id (drives batch/product auto-fill)
  batchlotno: string;
  productname: string;
  manufacturingdate: string;
  expiryretestdate: string;
  storedfromdate: string;
  storedtodate: string;
  temperaturemonitoring: "yes" | "no" | "";
  dataloggerid?: string | null;
  storageremarks?: string | null;
}

/* ------------------------------------------------------------------------ */
/* SECTION 1 FIELD CONFIGS                                                  */
/* ------------------------------------------------------------------------ */

export const getStudyProductFieldsRow1 = (studyOptions: any[]): FormFieldConfig[] => [
  {
    name: "studycode",
    label: "Study Code",
    type: "select",
    required: true,
    options: studyOptions,
    disabled: true,
    placeholder: "Select study code...",
  },
  {
    name: "sponsorname",
    label: "Sponsor Name",
    type: "text",
    placeholder: "Select Study",
    disabled: true,
  },
];

export const getIpCategoryField = (
  disabled: boolean,
  categoryOptions: any[]
): FormFieldConfig[] => [
  {
    name: "ipcategory",
    label: "IP Category",
    type: "radio",
    required: true,
    disabled,
    options: categoryOptions,
  },
];

export const getBatchAndProductFields = (
): FormFieldConfig[] => [
  {
    name: "batchlotno",
    label: "Batch No.",
    type: "text",
    required: true,
    disabled:true,
    placeholder: "Select IP Category",
  },
  {
    name: "productname",
    label: "Product Name",
    type: "text",
    disabled: true,
    placeholder: "Select IP Category",
  },
  {
    name: "manufacturingdate",
    label: "Manufacturing Date",
    type: "date",
    disabled: true,
    placeholder: "Select IP Category",
  },
  {
    name: "expiryretestdate",
    label: "Expiry / Retest Date",
    type: "date",
    disabled: true,
    placeholder: "Select IP Category",
  },
];

/* ------------------------------------------------------------------------ */
/* SECTION 3: STORAGE DETAILS FIELD CONFIGS                                 */
/* ------------------------------------------------------------------------ */

export const getStorageDetailsFields = (
  disabled: boolean,
  storedFromDate: string,
  isTemperatureRequired: string,
): FormFieldConfig[] => {
  const fields: FormFieldConfig[] = [
    {
      name: "storedfromdate",
      label: "Stored From Date",
      type: "flatpickr",
      required: true,
      disabled,
    },
    {
      name: "storedtodate",
      label: "Stored To Date",
      type: "flatpickr",
      required: true,
      optionsProps:{
        minDate :  parseLocalDate(storedFromDate),
      },
      disabled,
    },
    {
      name: "temperaturemonitoring",
      label: "Temperature Monitoring Required?",
      type: "radio",
      required: true,
      disabled,
      options: [
        { label: "Yes", value: "yes" },
        { label: "No", value: "no" },
      ],
    },
  ];
  if(isTemperatureRequired === 'yes'){
    fields.push(
      {
        name: "dataloggerid",
        label: "Data Logger ID",
        type: "text",
        required: true,
        disabled,
        placeholder: "Enter data logger ID...",
      },
    )
  }
  if(isTemperatureRequired === 'no'){
    fields.push(
      {
        name: "storageremarks",
        label: "Remarks",
        type: "textarea",
        disabled,
        placeholder: "Enter remarks...",
      },
    )
  }
  return fields;
};

/* ------------------------------------------------------------------------ */
/* CONTAINER STORAGE TABLE COLUMNS                                          */
/* ------------------------------------------------------------------------ */

export const getStorageContainerColumns = (
  containersList: StorageContainerItem[],
  handleChange: (index: number, field: keyof StorageContainerItem, value: any) => void,
  isDisabled: boolean = false
): Column[] => [
  {
    key: "containernumber",
    label: "Container Number",
    width: "140px",
    textCenter: true,
    render: (_val: any, row: any) => (
      <div className="d-flex align-items-center justify-content-center">
        <span className="fw-semibold px-2.5 py-1 bg-light rounded-pill border text-primary" style={{ fontSize: "11px" }}>
          Container {row?.containernumber ?? ""}
        </span>
      </div>
    ),
  },
  {
    key: "containerquantity",
    label: "Container Quantity",
    width: "140px",
    textCenter: true,
    render: (_val: any, row: any) => (
      <div className="d-flex align-items-center justify-content-center">
        <span className="fw-medium text-dark">{row?.containerquantity ?? row?.initialquantity ?? 0}</span>
      </div>
    ),
  },
  {
    key: "storagelocation",
    label: "Storage Location",
    render: (val: any, _row: any, index = 0) => (
      <select
        className="form-select form-select-sm"
        value={val ?? ""}
        disabled={isDisabled}
        onChange={(e) => handleChange(index, "storagelocation", e.target.value)}
      >
        {/* <option value="hello">Select...</option> */}
        {STORAGE_LOCATION_OPTIONS.map((opt: any) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    ),
  },
  {
    key: "storageequipment",
    label: "Storage Equipment",
    render: (val: any, _row: any, index = 0) => (
      <select
        className="form-select form-select-sm"
        value={val ?? ""}
        disabled={isDisabled}
        onChange={(e) => handleChange(index, "storageequipment", e.target.value)}
      >
        {/* <option value="">Select...</option> */}
        {STORAGE_EQUIPMENT_OPTIONS.map((opt: any) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    ),
  },
  {
    key: "shelfrack",
    label: "Shelf / Rack",
    render: (val: any, _row: any, index = 0) => (
      <input
        type="text"
        className="form-control form-control-sm"
        value={val ?? ""}
        disabled={isDisabled}
        onChange={(e) => handleChange(index, "shelfrack", e.target.value)}
        placeholder="Enter shelf/rack..."
      />
    ),
  },
  {
    key: "position",
    label: "Position",
    render: (val: any, _row: any, index = 0) => (
      <input
        type="text"
        className="form-control form-control-sm"
        value={val ?? ""}
        disabled={isDisabled}
        onChange={(e) => handleChange(index, "position", e.target.value)}
        placeholder="Enter position..."
      />
    ),
  },
  {
    key: "storagestatus",
    label: "Storage Status",
    render: (val: any, _row: any, index = 0) => (
      <select
        className="form-select form-select-sm"
        value={val ?? "Active"}
        disabled={isDisabled}
        onChange={(e) => handleChange(index, "storagestatus", e.target.value)}
      >
        {STORAGE_STATUS_OPTIONS.map((opt: any) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    ),
  },
  {
    key: "remarks",
    label: "Remarks",
    render: (val: any, _row: any, index = 0) => (
      <input
        type="text"
        className="form-control form-control-sm"
        value={val ?? ""}
        disabled={isDisabled}
        onChange={(e) => handleChange(index, "remarks", e.target.value)}
        placeholder="Enter remarks..."
      />
    ),
  },
];
